"use client"
import {
  type UseQueryResult,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { useCallback, useMemo } from "react"
import { Address, Evaluate } from "viem"
import { useChainId, useConfig } from "wagmi"
import {
  type GetUnitOfAccountPriceErrorType,
  getUnitOfAccountPrice,
} from "../actions/getUnitOfAccountPrice"
import {
  type GetUnitOfAccountPriceData,
  type GetUnitOfAccountPriceOptions,
  type GetUnitOfAccountPriceQueryFnData,
  type GetUnitOfAccountPriceQueryKey,
  getUnitOfAccountPriceQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UseUnitOfAccountPricesProps<
  config extends ExtendedConfig = ExtendedConfig,
  selectedData = GetUnitOfAccountPriceData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: Evaluate<
    GetUnitOfAccountPriceOptions &
      ConfigParameter<config> & {
        query?:
          | Evaluate<
              Omit<
                UseQueryParameters<
                  GetUnitOfAccountPriceQueryFnData,
                  GetUnitOfAccountPriceErrorType,
                  selectedData,
                  GetUnitOfAccountPriceQueryKey
                >,
                "gcTime" | "staleTime"
              >
            >
          | undefined
      }
  >[]
  select?: (data: GetUnitOfAccountPriceData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function useUnitOfAccountPrices<
  config extends ExtendedConfig,
  selectedData = GetUnitOfAccountPriceData,
  combineData = UseQueryResult<selectedData>[],
>(
  parameters: UseUnitOfAccountPricesProps<config, selectedData, combineData>,
): combineData {
  const { queries = [], select, combine } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })
  const queryResults = useQueries({
    queries: queries.map(({ query = {}, ...options }) => ({
      ...query,
      queryKey: getUnitOfAccountPriceQueryKey({
        ...options,
        chainId: options.chainId || chainId,
      }),
      queryFn: () =>
        getUnitOfAccountPrice(config, {
          ...options,
          chainId: options.chainId || chainId,
        }),
      select: select,
      enabled: Boolean(options.unitOfAccount && (query.enabled ?? true)),
      refetchOnMount: false,
      retry: false,
    })),
  }) satisfies UseQueryResult<selectedData>[]

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}

export function useInvalidateUnitOfAccountPricesQueryKeys() {
  const queryClient = useQueryClient()
  const chainId = useChainId()

  return useCallback(
    (unitOfAccounts?: Address[]) => {
      if (unitOfAccounts && unitOfAccounts.length > 0) {
        // Invalidate specific unit of account queries
        unitOfAccounts.forEach((unitOfAccount) => {
          const queryKey = getUnitOfAccountPriceQueryKey({
            unitOfAccount,
            chainId,
          })
          queryClient.invalidateQueries({ queryKey })
        })
      } else {
        // Invalidate all unit of account price queries
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "unitOfAccountPrice",
        })
      }
    },
    [queryClient, chainId],
  )
}
